/**
 * Navigation Service
 * Provides all navigation functionality for the ZEOS application
 *
 * This service provides:
 * - URL building and navigation utilities
 * - Onboarding step management
 * - Query string utilities
 * - Page navigation logic
 */

import { NavigateFunction } from "react-router-dom";

import { OnboardStepsConfig } from "../../configs/OnboardStepsConfig";
import { ZeosPageConfig } from "../../configs/PageConfig";
import { createLogger } from "../Logger";
import { OnboardHelpers } from "../onboarding/OnboardHelpers";
import { OnboardStepEnum } from "../zeos-api/enums/Onboarding";
import { isDemo } from "../environment/EnvironmentHelpers";
import { ONBOARDING_BASE_PATH } from "./Constants";
import {
  IOnboardNavStep,
  IOnboardNavSubstep,
  IOnboardPageManager,
} from "./Interfaces";
import { NavSlugType, OnboardNavSlugType, ZeosSearchParamType } from "./Types";

const log = createLogger("NavigationService");

// ===== CORE NAVIGATION SERVICE =====

/**
 * Main navigation service class that provides all navigation functionality
 */
export class NavigationService {
  // ===== PAGE CONFIGURATION UTILITIES =====

  /**
   * Get page configuration by slug
   */
  static pageConfigBySlug = (slug: NavSlugType) => {
    const config = ZeosPageConfig.find((x) => x.slug === slug);
    if (!config) {
      log.warn(`Page config not found for slug: ${slug}`);
      return null;
    }
    return config;
  };
  static qsMaker: any;
  static getSearchParamTyped: any;
  static getIsBackPage: any;

  // ===== URL BUILDING =====

  /**
   * Build onboarding URL from page slug
   */
  static buildOnboardingUrl(pageSlug: string, mode?: "back"): string {
    const baseUrl = `${ONBOARDING_BASE_PATH}/${pageSlug}`;
    return mode === "back" ? `${baseUrl}?mode=back` : baseUrl;
  }

  // ===== PAGE NAVIGATION =====

  /**
   * Get next page URL with business logic
   */
  static getNextPageUrl(
    pageSlug: string,
    pageManager: IOnboardPageManager
  ): string {
    if ((pageSlug === "survey" || pageSlug === "surveys") && isDemo()) {
      return NavigationService.buildOnboardingUrl("summary");
    }
    // Special case for UAPI flow - skip uapi-confirm temporarily
    if (pageSlug === "uapi") {
      return NavigationService.buildOnboardingUrl("summary");
    }
    return NavigationService.buildOnboardingUrl(pageManager.slugNext);
  }

  /**
   * Get previous page URL
   */
  static getPreviousPageUrl(pageManager: IOnboardPageManager): string {
    // FE-only survey substep and the page after surveys must return to the hub,
    // not to /onboarding/survey (which requires :domainSlug).
    if (pageManager.slug === "survey" || pageManager.slug === "uapi") {
      return NavigationService.buildOnboardingUrl("surveys", "back");
    }
    return NavigationService.buildOnboardingUrl(pageManager.slugPrev, "back");
  }

  // ===== NAVIGATION EXECUTION =====

  /**
   * Navigate with optional state (unified navigation method)
   */
  static navigateWithState(
    navigate: NavigateFunction,
    url: string,
    state?: any
  ): void {
    if (state) {
      navigate(url, { state });
    } else {
      navigate(url);
    }
  }

  // ===== ONBOARDING NAVIGATION LOGIC =====

  // ===== STEP MANAGEMENT =====

  // Step manager cache
  private static _navStepManager: IOnboardPageManager[] = [];

  /**
   * Get transformed onboard step config from OnboardStepsConfig
   * Always uses direct reference to OnboardStepsConfig - no caching needed
   */
  private static getOnboardStepConfig(): IOnboardNavStep[] {
    try {
      return OnboardHelpers.transformStaticHierarchyToNavSteps(
        OnboardStepsConfig
      );
    } catch (error) {
      log.error("Error transforming static hierarchy data:", error);
      return NavigationService.getFallbackConfig();
    }
  }

  /**
   * Fallback configuration when static hierarchy data is not available
   */
  private static getFallbackConfig(): IOnboardNavStep[] {
    return [
      {
        stepName: "Account",
        stepNum: OnboardStepEnum.Account,
        subSteps: [
          {
            pageSlug: "account-setup",
            subStepName: "Account",
            subStepNum: 1,
            showBackButton: false,
          },
          {
            pageSlug: "account-verification",
            subStepName: "Verify Email",
            subStepNum: 2,
            showBackButton: false,
          },
          {
            pageSlug: "account-password",
            subStepName: "Create Password",
            subStepNum: 3,
            showBackButton: false,
          },
        ],
      },
      {
        stepName: "Company",
        stepNum: OnboardStepEnum.Company,
        subSteps: [
          {
            pageSlug: "company",
            subStepName: "Company",
            subStepNum: 1,
            showBackButton: false,
          },
        ],
      },
      {
        stepName: "Property",
        stepNum: OnboardStepEnum.Property,
        subSteps: [
          {
            pageSlug: "property",
            subStepName: "Property",
            subStepNum: 1,
            showBackButton: true,
          },
        ],
      },
      {
        stepName: "Surveys",
        stepNum: OnboardStepEnum.EVSE,
        subSteps: [
          {
            pageSlug: "surveys",
            subStepName: "Optional Property Surveys",
            subStepNum: 1,
            showBackButton: true,
          },
          {
            pageSlug: "survey",
            subStepName: "Property Survey",
            subStepNum: 2,
            showBackButton: true,
          },
        ],
      },
      {
        stepName: "Utility Connection",
        stepNum: OnboardStepEnum.UtilityConnection,
        subSteps: [
          {
            pageSlug: "uapi",
            subStepName: "Utility Connection",
            subStepNum: 1,
            showBackButton: true,
          },
          {
            pageSlug: "uapi-manual",
            subStepName: "Utility Entry Manual",
            subStepNum: 2,
            showBackButton: true,
          },
          // {
          //   pageSlug: "uapi-confirm",
          //   subStepName: "Utility Confirmation",
          //   subStepNum: 3,
          //   showBackButton: true,
          // },
        ],
      },
      {
        stepName: "Summary",
        stepNum: OnboardStepEnum.Summary,
        subSteps: [
          {
            pageSlug: "summary",
            subStepName: "Summary",
            subStepNum: 1,
            showBackButton: true,
          },
        ],
      },
    ];
  }

  /**
   * Get step manager by step number
   */
  static navStepManagerByStepNum = (stepNum: OnboardStepEnum) => {
    const step = NavigationService._navStepManager.find(
      (x) => x.stepNum === stepNum
    );
    if (!step) {
      log.warn(`Step manager not found for step number: ${stepNum}`);
      return null;
    }
    return step;
  };

  /**
   * Get step manager by slug
   */
  static navStepManagerBySlug = (slug: OnboardNavSlugType) => {
    NavigationService._navStepManager.length === 0 &&
      NavigationService.navStepManager();
    const step = NavigationService._navStepManager.find((x) => x.slug === slug);
    if (!step) {
      log.warn(`Step manager not found for slug: ${slug}`);
      return null;
    }
    return step;
  };

  /**
   * Get substep by slug
   */
  static navSubstepBySlug = (slug: OnboardNavSlugType) => {
    const config = NavigationService.getOnboardStepConfig();
    return config
      .flatMap((step) => step.subSteps)
      .find((sub) => sub.pageSlug === slug);
  };

  /**
   * Initialize and get step manager
   */
  static navStepManager = () => {
    if (NavigationService._navStepManager.length > 0) {
      return NavigationService._navStepManager;
    }

    const configs = NavigationService.getOnboardStepConfig();
    if (configs.length === 0) {
      log.warn("No onboard step config available");
      return NavigationService._navStepManager;
    }

    // Sort configs by step number
    const sortedConfigs = [...configs].sort((a, b) => a.stepNum - b.stepNum);
    sortedConfigs.forEach((x) => {
      x.subSteps = [...x.subSteps].sort(
        (a: IOnboardNavSubstep, b: IOnboardNavSubstep) =>
          a.subStepNum - b.subStepNum
      );
    });

    sortedConfigs.forEach((step) => {
      step.subSteps.forEach((substep: IOnboardNavSubstep) => {
        NavigationService._navStepManager.push(
          NavigationService.stepToPageManager(
            NavigationService._navStepManager.length,
            step,
            substep
          )
        );
      });
    });

    NavigationService._navStepManager.forEach((x) => {
      x.stepTotal = sortedConfigs.length;
      x.slugNext = x.isLast
        ? ""
        : NavigationService._navStepManager[x.orderNumNext]?.slug || "";
      x.slugPrev = x.isFirst
        ? ""
        : NavigationService._navStepManager[x.orderNumPrev]?.slug || "";
    });

    return NavigationService._navStepManager;
  };

  /**
   * Convert step and substep to page manager
   */
  private static stepToPageManager(
    pos: number,
    step: IOnboardNavStep,
    substep: IOnboardNavSubstep
  ) {
    const pm = {} as IOnboardPageManager;
    pm.slug = substep.pageSlug;
    pm.stepTitle = step.stepName;
    const pageConfig = NavigationService.pageConfigBySlug(substep.pageSlug);
    pm.pageTitle = pageConfig?.pageTitle || "";
    pm.pageMessage = pageConfig?.pageMessage || "";
    pm.stepNum = step.stepNum;
    pm.stepNumNext = step.stepNum + 1;
    pm.stepNumPrev = step.stepNum - 1;
    pm.isFirst = step.stepNum == OnboardStepEnum.Account;
    pm.isLast = step.stepNum == OnboardStepEnum.Summary;
    pm.orderNum = pos;
    pm.orderNumNext = pos + 1;
    pm.orderNumPrev = pos - 1;
    return pm;
  }
}

// ===== LEGACY EXPORTS FOR BACKWARD COMPATIBILITY =====

// Export individual functions for backward compatibility
export const pageConfigBySlug = NavigationService.pageConfigBySlug;
export const buildOnboardingUrl = NavigationService.buildOnboardingUrl;
export const getNextPageUrl = NavigationService.getNextPageUrl;
export const getPreviousPageUrl = NavigationService.getPreviousPageUrl;
export const navigateWithState = NavigationService.navigateWithState;
export const navStepManagerByStepNum =
  NavigationService.navStepManagerByStepNum;
export const navStepManagerBySlug = NavigationService.navStepManagerBySlug;
export const navSubstepBySlug = NavigationService.navSubstepBySlug;
export const navStepManager = NavigationService.navStepManager;
